/**
 * Sub-panel embedido dentro del ClaimPanel para interactuar con el Claim
 * Assistant Agent (backend /api/claim-assistant/*).
 *
 * Flujo:
 *   1. Al montar, ensureSession() crea o recupera sesión por (claim, seller).
 *   2. Operador escribe un prompt → streamTurn() → eventos SSE pintan en vivo.
 *   3. Si el agente llama propose_draft, los drafts aparecen como cards aprobables.
 *   4. Click "Aprobar y enviar" → approve() → ML recibe el mensaje real.
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles, Send, Loader2, CheckCircle2, AlertCircle, X, Trash2 } from 'lucide-react';
import {
  useClaimAssistant,
  type DraftCandidate,
  type StreamChunk,
} from '../../hooks/useClaimAssistant';
import { Can } from '../../lib/rbac';

interface Props {
  mlClaimId: number;
  sellerId: number;
  onClose?: () => void;
  /** Base alternativa del asistente (mini-app firmada). Default: '/api/claim-assistant'. */
  baseUrl?: string;
  /** Token firmado del enlace (mini-app). Si se pasa, auth por Bearer en vez de cookie. */
  token?: string;
}

interface StreamLine {
  kind: 'text' | 'tool_use' | 'tool_result' | 'final' | 'error';
  label: string;
}

// Título humano de cada acción real propuesta (cards ámbar).
const ACTION_TITLES: Record<string, string> = {
  allow_return: 'Autorizar devolución',
  open_dispute: 'Escalar a mediación',
  upload_shipping_evidence: 'Subir evidencia de envío',
};

export function AssistantPanel({ mlClaimId, sellerId, onClose, baseUrl, token }: Props) {
  const ca = useClaimAssistant({ mlClaimId, sellerId, baseUrl, token });
  const [prompt, setPrompt] = useState('Analizá este claim y propone 3 borradores.');
  const [lines, setLines] = useState<StreamLine[]>([]);
  const [bootstrapped, setBootstrapped] = useState(false);
  // Aprobados por message_id (backend nuevo) o índice (fallback backend viejo).
  // Con índice solo, al reemplazarse la tanda un draft NUEVO heredaba el "Enviado".
  const [approving, setApproving] = useState<string | null>(null);
  const [approvedKeys, setApprovedKeys] = useState<Set<string>>(new Set());
  const [showOld, setShowOld] = useState(false);
  // Acciones reales propuestas por el bot (propose_action).
  const [actionRunning, setActionRunning] = useState<string | null>(null);
  const [executedActions, setExecutedActions] = useState<Set<string>>(new Set());
  // Si ML rechazó la acción vía API (CBT): link para hacerla a mano en la web.
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-init sesión al montar.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ca.ensureSession();
        if (!cancelled) setBootstrapped(true);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : String(err);
          setLines((p) => [...p, { kind: 'error', label: `init: ${msg}` }]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mlClaimId, sellerId]);

  // Auto-scroll al fondo cuando llegan eventos.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [lines]);

  function onChunk(c: StreamChunk) {
    if (c.kind === 'text') {
      setLines((prev) => {
        // Append al último text si es seguido — evita 1 línea por token.
        const last = prev[prev.length - 1];
        if (last && last.kind === 'text') {
          return [...prev.slice(0, -1), { kind: 'text', label: last.label + c.text }];
        }
        return [...prev, { kind: 'text', label: c.text }];
      });
    } else if (c.kind === 'tool_use') {
      setLines((p) => [...p, { kind: 'tool_use', label: `→ ${c.name}(${shortJson(c.input)})` }]);
    } else if (c.kind === 'tool_result') {
      setLines((p) => [
        ...p,
        { kind: 'tool_result', label: `← ${c.ok ? 'ok' : 'err'}: ${c.preview.slice(0, 100)}` },
      ]);
    } else if (c.kind === 'final') {
      setLines((p) => [
        ...p,
        {
          kind: 'final',
          label: `Turn done — $${c.cost_usd.toFixed(4)} (${c.tokens_in}→${c.tokens_out} tok)`,
        },
      ]);
    } else if (c.kind === 'error') {
      setLines((p) => [...p, { kind: 'error', label: c.message }]);
    }
  }

  async function handleSend() {
    if (!prompt.trim() || ca.streaming || !ca.session) return;
    const userPrompt = prompt;
    setPrompt('');
    setLines((p) => [...p, { kind: 'text', label: `\n[Vos] ${userPrompt}\n` }]);
    await ca.streamTurn(userPrompt, onChunk);
  }

  async function handleApprove(draft: DraftCandidate, key: string) {
    if (draft.receiver_role === 'respondent') {
      alert('respondent no es válido para enviar — el agente debería haber emitido complainant o mediator');
      return;
    }
    setApproving(key);
    try {
      await ca.approve(
        { text: draft.text, receiver_role: draft.receiver_role as 'complainant' | 'mediator' },
        'ui-operator'
      );
      setApprovedKeys((s) => new Set(s).add(key));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert(`No se pudo enviar: ${msg}`);
    } finally {
      setApproving(null);
    }
  }

  return (
    <div className="flex flex-col h-full bg-[var(--color-surface)]">
      {/* Header */}
      <div className="px-3 py-2 border-b border-[var(--color-border)] flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-purple-600" />
          <span className="text-xs font-semibold text-[var(--color-text)]">
            Asistente AI {ca.session ? `#${ca.session.id}` : ''}
          </span>
          {ca.session && (
            <span className="text-[10px] text-[var(--color-text-muted)]">
              ${ca.session.total_cost_usd?.toFixed(4) ?? '0.0000'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* "Empezar de cero" (pedido usuario 2026-07-04): reemplaza al viejo
              refrescar. Borra en DB mensajes+borradores de la sesión y deja al
              motor sin memoria. Lo ENVIADO a ML no se borra (registro legal). */}
          <button
            onClick={async () => {
              if (!ca.session || ca.streaming) return;
              if (
                !window.confirm(
                  '¿Borrar toda la conversación y los borradores de este reclamo? No se puede deshacer. (Lo ya enviado a Mercado Libre se conserva en la auditoría.)'
                )
              )
                return;
              try {
                await ca.resetSession();
                setLines([]);
                setApprovedKeys(new Set());
                setApproving(null);
                setShowOld(false);
                setExecutedActions(new Set());
                setActionRunning(null);
                setManualUrl(null);
              } catch (err) {
                alert(err instanceof Error ? err.message : String(err));
              }
            }}
            disabled={!ca.session || ca.streaming}
            className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-red-500"
            title="Empezar de cero (borra la conversación y los borradores)"
          >
            <Trash2 size={12} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
              title="Cerrar asistente"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Stream output */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-2 space-y-1 font-mono text-[11px]">
        {!bootstrapped && (
          <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
            <Loader2 size={12} className="animate-spin" /> Iniciando sesión...
          </div>
        )}
        {lines.map((l, i) => (
          <div
            key={i}
            className={
              l.kind === 'tool_use'
                ? 'text-blue-700 dark:text-blue-300'
                : l.kind === 'tool_result'
                  ? 'text-[var(--color-text-muted)] pl-3'
                  : l.kind === 'error'
                    ? 'text-red-600'
                    : l.kind === 'final'
                      ? 'text-green-600 italic'
                      : 'text-[var(--color-text)] whitespace-pre-wrap'
            }
          >
            {l.label}
          </div>
        ))}
        {ca.streaming && (
          <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
            <Loader2 size={12} className="animate-spin" /> pensando...
          </div>
        )}
      </div>

      {/* Drafts aprobables */}
      {ca.drafts.length > 0 && (
        <div className="px-2 py-2 border-t border-[var(--color-border)] space-y-1.5 max-h-[40%] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Borradores propuestos ({ca.drafts.length})
          </div>
          {ca.drafts.map((d, idx) => {
            const key = d.message_id != null ? `m${d.message_id}` : `i${idx}`;
            const sent = approvedKeys.has(key);
            const isApproving = approving === key;
            return (
              <div
                key={key}
                className={`p-2 rounded border text-xs ${
                  sent
                    ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                    : 'border-[var(--color-border)] bg-[var(--color-bg)]'
                }`}
              >
                <div className="flex items-center justify-between mb-1 text-[10px] text-[var(--color-text-muted)]">
                  <span>
                    <strong>{d.tone}</strong> · {d.receiver_role} · {d.char_count}c
                  </span>
                  <span title={d.strategy} className="truncate max-w-[180px] italic">
                    {d.strategy}
                  </span>
                </div>
                <div className="text-[var(--color-text)] mb-1.5 whitespace-pre-wrap">{d.text}</div>
                <Can cap="orders.claims_manage">
                <button
                  onClick={() => handleApprove(d, key)}
                  disabled={sent || isApproving || ca.streaming}
                  className={`w-full inline-flex items-center justify-center gap-1.5 px-2 py-1 text-[11px] font-medium rounded transition-opacity ${
                    sent
                      ? 'bg-green-600 text-white cursor-default'
                      : 'bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50'
                  }`}
                >
                  {isApproving ? (
                    <>
                      <Loader2 size={11} className="animate-spin" /> Enviando...
                    </>
                  ) : sent ? (
                    <>
                      <CheckCircle2 size={11} /> Enviado
                    </>
                  ) : (
                    <>
                      <Send size={11} /> Aprobar y enviar
                    </>
                  )}
                </button>
                </Can>
              </div>
            );
          })}
        </div>
      )}

      {/* Acciones reales propuestas (propose_action) — cards ámbar con confirmación.
          max-h + overflow propio: nunca empujar el input fuera del viewport
          (regresión del bug 2026-07-02 en ventanas bajas). */}
      {ca.actionCandidates.length > 0 && (
        <div className="px-2 py-2 border-t border-[var(--color-border)] space-y-1.5 max-h-[30%] overflow-y-auto">
          {ca.actionCandidates.map((a, i) => {
            const key = a.message_id != null ? `am${a.message_id}` : `ai${i}`;
            const done = executedActions.has(key);
            return (
              <div
                key={key}
                className="border-2 border-amber-500 rounded p-2 bg-amber-500/10 text-xs"
              >
                <div className="text-xs font-bold text-amber-600 uppercase mb-1">
                  ⚠ Acción propuesta:{' '}
                  {ACTION_TITLES[a.action_type] ?? a.action_type}
                </div>
                <p className="text-xs mb-1 text-[var(--color-text)]">
                  <b>Por qué:</b> {a.justificacion}
                </p>
                <p className="text-xs mb-2 text-[var(--color-text)]">
                  <b>Qué va a pasar:</b> {a.resultado_esperado}
                </p>
                {/* Evidencia de envío: mostrar EXACTAMENTE qué se va a mandar
                    (es inmutable una vez enviada — el operador revisa antes) */}
                {a.envio && (
                  <div className="text-xs mb-2 text-[var(--color-text)] border border-amber-500/40 rounded p-1.5 bg-amber-500/5">
                    <b>Datos que se envían a Mercado Libre:</b>
                    <div>Transportista: {a.envio.shipping_company_name}</div>
                    {a.envio.tracking_number && <div>Seguimiento: {a.envio.tracking_number}</div>}
                    <div>
                      Fecha de despacho:{' '}
                      {new Date(a.envio.date_shipped).toLocaleDateString('es-AR')}
                    </div>
                  </div>
                )}
                <Can cap="orders.claims_manage">
                <button
                  className="text-xs bg-amber-600 text-white rounded px-2 py-1 disabled:opacity-50"
                  disabled={actionRunning !== null || done || ca.streaming}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Esta acción se ejecuta EN Mercado Libre y no se puede deshacer. ¿Confirmás?'
                      )
                    )
                      return;
                    setActionRunning(a.action_type);
                    try {
                      const out = await ca.executeAction(a.action_type);
                      if (out.manual_required) {
                        setManualUrl(out.web_url ?? null);
                        alert(
                          'Mercado Libre no permite ejecutar esta acción desde el sistema. ' +
                            'Hay que hacerla a mano desde la página de la venta en Mercado Libre.'
                        );
                      } else {
                        setExecutedActions((s) => new Set(s).add(key));
                        alert(
                          'Ejecutada. Verificación: ' +
                            JSON.stringify(out.data?.verification?.despues ?? {})
                        );
                      }
                    } catch (e) {
                      alert('Error: ' + (e as Error).message);
                    } finally {
                      setActionRunning(null);
                    }
                  }}
                >
                  {actionRunning === a.action_type
                    ? 'Ejecutando…'
                    : done
                      ? 'Ejecutada ✓'
                      : 'Aprobar y ejecutar'}
                </button>
                </Can>
                {manualUrl && (
                  <a
                    href={manualUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-block text-xs bg-[var(--color-primary)] text-white rounded px-2 py-1"
                  >
                    Abrir en Mercado Libre
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Borradores reemplazados (tandas anteriores) — solo lectura, sin aprobar */}
      {ca.supersededDrafts.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowOld((v) => !v)}
            className="text-xs text-[var(--color-text-secondary)] underline"
          >
            {showOld
              ? 'Ocultar borradores anteriores'
              : `Ver borradores anteriores (${ca.supersededDrafts.length})`}
          </button>
          {/* Cap de altura en la lista expandida: sin esto, tandas largas empujan el
              input fuera del viewport en ventanas bajas (regresión del bug 2026-07-02).
              30vh y no % porque el wrapper padre tiene altura auto y un max-h
              porcentual no aplicaría. */}
          {showOld && (
            <div className="mt-1 space-y-1 opacity-60 max-h-[30vh] overflow-y-auto">
              {ca.supersededDrafts.map((d, i) => (
                <div
                  key={`old-${i}`}
                  className="border border-dashed border-[var(--color-border)] rounded p-2 text-xs"
                >
                  <span className="font-semibold mr-1">[reemplazado]</span>
                  {d.text}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {ca.error && (
        <div className="px-3 py-1.5 bg-red-50 dark:bg-red-950/30 border-t border-red-200 dark:border-red-800 text-[11px] text-red-700 dark:text-red-300 flex items-center gap-1.5">
          <AlertCircle size={12} /> {ca.error}
        </div>
      )}

      {/* Input */}
      <div className="px-2 py-2 border-t border-[var(--color-border)] flex items-end gap-1.5">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Pedile al asistente: analizá, propone borradores, validá..."
          className="flex-1 text-xs p-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] resize-none outline-none focus:border-[var(--color-primary)]"
          rows={2}
          disabled={ca.streaming || !ca.session}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button
          onClick={handleSend}
          disabled={!prompt.trim() || ca.streaming || !ca.session}
          className="h-8 w-8 flex items-center justify-center rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 flex-shrink-0"
          title="Enviar (Ctrl+Enter)"
        >
          {ca.streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
    </div>
  );
}

function shortJson(o: Record<string, unknown>): string {
  const s = JSON.stringify(o);
  return s.length > 60 ? s.slice(0, 60) + '...' : s;
}
